import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutGrid, DoorOpen, ClipboardList, LogOut, ShieldCheck, Sun, LayoutDashboard, Wrench, Inbox, CircleDollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export const ViewNav = () => {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [overdue, setOverdue] = useState(0);

  useEffect(() => {
    let active = true;
    const load = () => {
      api.getUnreadReplies().then((d) => { if (active) setUnread(d.count || 0); }).catch(() => {});
      api.getOverdueSummary().then((d) => { if (active) setOverdue((d.overdue_count || 0) + (d.unmatched_count || 0)); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const items = [
    { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
    { to: "/units", label: "Units", icon: LayoutGrid, testid: "nav-units" },
    { to: "/availability", label: "Availability", icon: DoorOpen, testid: "nav-availability" },
    { to: "/waitlist", label: "Waitlist", icon: ClipboardList, testid: "nav-waitlist" },
    { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
    { to: "/inbox", label: "Inbox", icon: Inbox, testid: "nav-inbox", badge: unread },
    { to: "/overdue", label: "Overdue", icon: CircleDollarSign, testid: "nav-overdue", badge: overdue },
    { to: "/sigenstor", label: "Energy", icon: Sun, testid: "nav-sigenstor" },
  ];
  if (user?.role === "admin") {
    items.push({ to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" });
  }
  return (
    <div className="flex items-center gap-2 min-w-0">
      <nav className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto max-w-full" data-testid="view-nav">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end
              data-testid={it.testid}
              className={({ isActive }) =>
                cn(
                  "relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap",
                  isActive ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" /> <span className="hidden lg:inline">{it.label}</span>
              {it.badge > 0 && (
                <span data-testid={`${it.testid}-badge`} className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-4 text-center">
                  {it.badge > 9 ? "9+" : it.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      {user && (
        <div className="flex items-center gap-2 pl-1">
          <span className="hidden md:block text-xs text-slate-500 max-w-[160px] truncate" data-testid="current-user">{user.email}</span>
          <button
            data-testid="logout-button"
            onClick={logout}
            title="Sign out"
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 border border-slate-200 hover:border-red-200 rounded-md px-2.5 py-1.5 transition-colors whitespace-nowrap"
          >
            <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
};
